import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  Menu,
  Select,
  Spin,
  Table,
  Tooltip,
} from "antd";
import { ErrorNotificationMsg } from "../../utils/Notifications";
import { getRequest } from "../../axios";
import { authUserData } from "../../utils/Helper";
import {
  STATIC_COMPARATIVE_RESPONSE,
  buildGeneralComparativeFilterPayload,
  buildGeneralComparativeSubmitPayload,
} from "./generalProcurementComparativeStatic";
import { InfoCircleOutlined } from "@ant-design/icons";

import ButtonField from "../../common/htmlComponents/ButtonField";
import Downloadpd from "../../image/Downloadpd.svg";
import Excel from "../../image/excel.png";
import PDF from "../../image/pdf.png";

import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import "jspdf-autotable";
import jsPDF from "jspdf";
import { DownloadTableExcel } from "react-export-table-to-excel";

export const SPECIFIC_GRAVITY = {
  GI: 7.85,
  MS: 7.85,
  CU: 8.9,
  AL: 2.7,
  SS: 8.0,
};

export const DENSITY = {
  GI: 7850,
  MS: 7850,
  CU: 8960,
  AL: 2700,
  SS: 8000,
};

export function getDensity(material) {
  const sg = SPECIFIC_GRAVITY[material.toUpperCase()] || 0;
  return sg * 1000; 
}

export function getKgPerMeter(widthMM, thicknessMM, material) {
  const density = DENSITY[material.toUpperCase()] || 0;
  if (!density || !widthMM || !thicknessMM) return 0;
  return (widthMM * thicknessMM * density) / 1_000_000;
}

const GeneralProcurementComparative = () => {
  const { t } = useTranslation();
  const { Option } = Select;
  const navigate = useNavigate();
  const CompanyId = authUserData()?.company ? authUserData()?.company : null;
  const [reload, setReload] = useState(false);
  const [materialName, setMaterialName] = useState([]);
  const [enableInputs, setEnableInputs] = useState(false);
  const { id } = useParams();
  const { Search } = Input;
  const [totalCount, settotalCount] = useState(0);
  const [rfqHistory, setRFQHistory] = useState([]);
  const [categoryTree, setCategoryTree] = useState("");
  const [siteDetails, setSiteDetails] = useState(null);
  const [initialRender, setInitialRender] = useState(true);
  const formRef = useRef();
  const [vendors, setVendors] = useState([]);
  const estPriceInitialized = useRef(false);

  const [loading, setLoading] = useState(false);
  const [apiLoading, setapiLoading] = useState(false);
  const [state, setState] = useState({
    material_type_names: [],
  });

  const [estPrice, setEstPrice] = useState({
    CU: 0,
    GI: 0,
  });

  const [hideInitialBidPrice, sethideInitialBidPrice] = useState(false);
  const [hideR1BidPrice, setHideR1BidPrice] = useState(false);
  const [hideRBidPrice, setHideRBidPrice] = useState(false);
  const [showUnitRateCementR1, setShowUnitRateCementR1] = useState(false);
  const [showUnitRateCementR, setShowUnitRateCementR] = useState(false);
  const [isFinalBidPrice, setIsFinalBidPrice] = useState(false);
  const [showAdditionalCheckboxesFinal, setShowAdditionalCheckboxesFinal] =
    useState(true);
  const [showAdditionalCheckboxes, setShowAdditionalCheckboxes] =
    useState(false);
  const [comparativesRFQ, setComparativesRFQ] = useState([]);
  const [rfqNumber, setRfqNumber] = useState(null);
  const [buyerName, setBuyerName] = useState(null);
  const [contengency, setContengency] = useState(null);
  const [ohpPercent, setOhpPercent] = useState(0);

  const [response1, setResponse1] = useState([]);
  const [dataSource1, setDataSource] = useState([]);
  const [data, setData] = useState([]);

  const [summaryDataSource, setSummaryDataSource] = useState([]);
  const [showR1, setShowR1] = useState(false);
  const [showR0, setShowR0] = useState(false);
  const [showR, setShowR] = useState(false);
  const [showLPP, setShowLPP] = useState(false);
  const [showGST, setShowGST] = useState(false);
  const tableRef = useRef();
  const hiddenTableRef = useRef();

  const [initialBidDate, setInitialBidDate] = useState(null);
  const [revisedBidDate, setRevisedBidDate] = useState(null);
  const [finalBidDate, setFinalBidDate] = useState(null);

  const [vendorBidDates, setVendorBidDates] = useState({});

  const getVendorBidRFQ = async () => {
    setapiLoading(true);
    try {
      const response1 = STATIC_COMPARATIVE_RESPONSE;
      setCategoryTree(response1.data.data.category_tree_name);
      setRfqNumber(response1.data.data.rfq_number);
      setBuyerName(response1.data.data.buyer_name);
      setContengency(response1.data.data.contingency_charges);
      setResponse1(response1);
      setOhpPercent(response1.data.data.supplier_overhead_profit_perc);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setapiLoading(false);
    }
  };

  useEffect(() => {
    getVendorBidRFQ();
  }, []);

  useEffect(() => {
    if (!response1?.data || estPriceInitialized.current) return;

    if (response1.data.data) {
      const materials =
        response1.data.data.comparative_details_by_rfq_site_wise?.[0]
          ?.rfq_site_wise_details?.[0]?.rfq_material_wise_details || [];

      const cuPrice = materials.find((m) =>
        m.rfq_material_type_name.includes("- Cu")
      )?.estimated_material_unit_price;

      const giPrice = materials.find((m) =>
        m.rfq_material_type_name.includes("- GI")
      )?.estimated_material_unit_price;

      setEstPrice({
        CU: cuPrice ?? 0,
        GI: giPrice ?? 0,
      });

      estPriceInitialized.current = true;
    }
  }, [response1]);

  useEffect(() => {
    if (!response1?.data) return;

    if (response1.data.data) {
      const dataSource = [];
      const summaryDataSource = [];
      let totalQuantity = 0;
      let materialRowIndex = 0;
      let uniqueKeyCounter = 0;
      const uniqueEntries = new Set();
      const selectedRounds = {};

      const getAvailableRound = (vendorDetail, vendorSubTotals) => {
        if (vendorDetail.bid_date === null) {
          return null; // No valid round available
        } else {
          const vendorKey = vendorDetail.vendor_name
            .toLowerCase()
            .replace(/\s+/g, "");

          if (selectedRounds[vendorKey]) {
            return selectedRounds[vendorKey];
          }

          const vendorRounds1 = vendorSubTotals.filter(
            (v) => v.vendor_name === vendorDetail.vendor_name
          );

          const vendorRounds = vendorSubTotals
            .filter(
              (v) =>
                v.vendor_name === vendorDetail.vendor_name &&
                vendorDetail.bid_date !== null
            )
            .map((v) => v.round);

          const priorityOrder = ["r", "r1", "r0"];
          const selectedRound = priorityOrder.find((round) =>
            vendorRounds.includes(round)
          );

          selectedRounds[vendorKey] = selectedRound || null;

          return selectedRound;
        }
      };

      const findLatestAvailableRound = (vendorDetail, vendorSubTotals) => {
        if (!vendorDetail) return null;

        const vendorRounds = vendorSubTotals
          .filter(
            (v) =>
              v.vendor_name === vendorDetail.vendor_name && v.bid_date !== null
          )
          .sort((a, b) => {
            const roundPriority = { r: 3, r1: 2, r0: 1 };
            return roundPriority[b.round] - roundPriority[a.round];
          });

        return vendorRounds.length > 0 ? vendorRounds[0].round : null;
      };

      const isSand =
        response1?.data?.data?.category_tree_name
          ?.toLowerCase()
          .includes("sand") ||
        response1?.data?.data?.category_tree_name
          ?.toLowerCase()
          .includes("aggregate");

      const isEarthing = response1?.data?.data?.category_tree_name
        ?.toLowerCase()
        .includes("earthing");

      const vendors =
        response1?.data?.data?.comparative_details_by_rfq_site_wise.map(
          (cityDetail, cityIndex) => {
            return cityDetail.rfq_site_wise_details.map(
              (siteDetail, siteIndex) => {
                siteDetail.rfq_material_wise_details?.forEach(
                  (materialDetail, materialIndex) => {
                    totalQuantity +=
                      materialDetail.rfq_material_type_total_quantity;

                    const materialVendors = materialDetail.vendors || [];

                    const vendorData = materialVendors.reduce((acc, vendor) => {
                      if (vendor) {
                        const vendorKey = `${vendor.vendor_name
                          .toLowerCase()
                          .replace(/\s+/g, "")}`;
                        const price = vendor?.r_price[0] || {};
                        const priceR0 = vendor?.r0_price[0] || {};
                        const priceR1 = vendor?.r1_price[0] || {};

                        const initialBidDate =
                          vendor.r0_price[0]?.bid_date || "N/A";
                        const revisedBidDate =
                          vendor.r1_price[0]?.bid_date || "N/A";
                        const finalBidDate =
                          vendor.r_price[0]?.bid_date || "N/A";

                        setVendorBidDates((prevState) => ({
                          ...prevState,
                          [vendorKey]: {
                            initialBidDate,
                            revisedBidDate,
                            finalBidDate,
                          },
                        }));

                        const unitPrice = price?.r_unit_price || Infinity;


                        if (isEarthing) {
                          const { width, thickness, material } =
                            parseEarthingMaterial(
                              materialDetail.rfq_material_type_name
                            );

                          const kgPerMeter = getKgPerMeter(
                            width,
                            thickness,
                            material
                          )
                          acc[`${vendorKey}QuotedRsPerKg`] =
                          (unitPrice/kgPerMeter).toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            }) || 0;

                            acc[`${vendorKey}QuotedRsPerKgR0`] =
                          (priceR0?.r_unit_price / kgPerMeter).toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            }) || 0;
                        acc[`${vendorKey}QuotedRsPerKgR1`] =
                          (priceR1?.r_unit_price / kgPerMeter).toLocaleString("en-IN", {
                              maximumFractionDigits: 0,
                            }) || 0;
                        }

                        acc[`${vendorKey}UnitPrice`] =
                          unitPrice.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;

                        
                        acc[`${vendorKey}Amount`] =
                          price?.r_amount_without_gst?.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;
                        acc[`${vendorKey}UnitPriceR0`] =
                          priceR0?.r_unit_price?.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;
                        acc[`${vendorKey}AmountR0`] =
                          priceR0?.r_amount_without_gst?.toLocaleString(
                            "en-IN",
                            {
                              maximumFractionDigits: 0,
                            }
                          ) || 0;
                        acc[`${vendorKey}UnitPriceR1`] =
                          priceR1?.r_unit_price?.toLocaleString("en-IN", {
                            maximumFractionDigits: 0,
                          }) || 0;
                        acc[`${vendorKey}AmountR1`] =
                          priceR1?.r_amount_without_gst?.toLocaleString(
                            "en-IN",
                            {
                              maximumFractionDigits: 0,
                            }
                          ) || 0;

                        acc.prices = acc.prices || {};
                        acc.prices[vendorKey] = unitPrice;
                      }
                      return acc;
                    }, {});

                    const vendorPrices = materialVendors.map((vendor) => {
                      const vendorKey = `${vendor.vendor_name
                        .toLowerCase()
                        .replace(/\s+/g, "")}`;
                      let latestPrice = Infinity;

                      if (
                        vendor.r_price[0]?.r_unit_price !== undefined &&
                        vendor.r_price[0]?.bid_date
                      ) {
                        latestPrice = vendor.r_price[0].r_unit_price;
                      } else if (
                        vendor.r1_price[0]?.r_unit_price !== undefined &&
                        vendor.r1_price[0]?.bid_date
                      ) {
                        latestPrice = vendor.r1_price[0].r_unit_price;
                      } else if (
                        vendor.r0_price[0]?.r_unit_price !== undefined &&
                        vendor.r0_price[0]?.bid_date
                      ) {
                        latestPrice = vendor.r0_price[0].r_unit_price;
                      }
                      return { vendorKey, price: latestPrice };
                    });
                    const minPriceVendor = vendorPrices.reduce(
                      (minVendor, currentVendor) => {
                        return currentVendor.price < minVendor.price
                          ? currentVendor
                          : minVendor;
                      },
                      { vendorKey: null, price: Infinity }
                    );

                    materialDetail.last_purchase_price?.forEach(
                      (priceDetail, priceIndex) => {
                        const uniqueId = `${materialDetail.rfq_material_type_name}-${materialDetail.rfq_material_type_id}-${priceDetail.vendor_name}`;

                        let wtKgPerMeter = null;
                        let estRate = 0;
                        let calculatedOhp = 0;
                        if (isEarthing) {
                          const { width, thickness, material } =
                            parseEarthingMaterial(
                              materialDetail.rfq_material_type_name
                            );

                          const kgPerMeter = getKgPerMeter(
                            width,
                            thickness,
                            material
                          );

                          let ratePerKg =
                            materialDetail.estimated_material_unit_price || 0;

                          if (material === "CU") {
                            ratePerKg = estPrice.CU;
                          } else if (material === "GI") {
                            ratePerKg = estPrice.GI;
                          }

                          const baseRatePerMeter = kgPerMeter * ratePerKg;

                          const supplierProfitPercent = 10; 
                          
                          estRate = baseRatePerMeter;
                       
                          calculatedOhp =
                            baseRatePerMeter +
                            baseRatePerMeter * (ohpPercent / 100);

                          wtKgPerMeter = kgPerMeter;
                        }

                        if (!uniqueEntries.has(uniqueId) && priceIndex === 0) {
                          uniqueEntries.add(uniqueId);

                          materialRowIndex += 1;
                          dataSource.push({
                            key: `lp-${cityIndex}-${siteIndex}-${materialIndex}-${priceIndex}-${uniqueKeyCounter++}`,
                            srNo: `${materialRowIndex}`,
                            items: materialDetail.rfq_material_type_name,
                            gst_rate: materialDetail.gst_rate,
                            qty: materialDetail.rfq_material_type_total_quantity,
                            uom: materialDetail.rfq_material_type_uom,
                            wtKgPerMeter: wtKgPerMeter ? wtKgPerMeter : "N/A",
                            estRate: estRate ? estRate : "N/A",
                            ohp: calculatedOhp ? calculatedOhp : 0,
                            materialId: materialDetail.rfq_material_type_id,
                            poDate: priceDetail?.lpp_po_number_date || "N/A",
                            vendorName: priceDetail.vendor_name,
                            unitPrice:
                              priceDetail?.unit_price?.toLocaleString("en-IN", {
                                maximumFractionDigits: 0,
                              }) || 0,
                            avgPrice:
                              priceDetail["30_days_avg_price"]?.toLocaleString(
                                "en-IN",
                                {
                                  maximumFractionDigits: 0,
                                }
                              ) || 0,
                            itemWiseL1:
                              priceDetail["item_wise_l1"]?.toLocaleString(
                                "en-IN",
                                { maximumFractionDigits: 0 }
                              ) || 0,
                            cityWise:
                              priceDetail[
                                "30_days_avg_price_sb"
                              ]?.toLocaleString("en-IN", {
                                maximumFractionDigits: 0,
                              }) || 0,
                            ...vendorData,
                          });
                        }
                      }
                    );

                    const findLowestPrices = (vendorSubTotals, keySuffix) => {
                      if (!vendorSubTotals || vendorSubTotals.length === 0)
                        return [];

                      const basicPrices = vendorSubTotals
                        .filter(
                          (vendorDetail) => keySuffix === "commercial_ranking"
                        )
                        .map((vendorDetail) => vendorDetail.basic_price)
                        .filter((price) => typeof price === "number");

                      const sortedPrices = [...new Set(basicPrices)].sort(
                        (a, b) => a - b
                      );

                      return sortedPrices.slice(0, 3);
                    };

                    const cityWiseList =
                      response1?.data?.data?.comparative_details_by_rfq_site_wise ||
                      [];
                    const lastCityIdx = Math.max(0, cityWiseList.length - 1);
                    const lastSiteIdx = Math.max(
                      0,
                      (cityWiseList[lastCityIdx]?.rfq_site_wise_details
                        ?.length ?? 1) - 1,
                    );
                    const lastMaterialIdx = Math.max(
                      0,
                      (cityWiseList[lastCityIdx]?.rfq_site_wise_details?.[
                        lastSiteIdx
                      ]?.rfq_material_wise_details?.length ?? 1) - 1,
                    );
                    const isLastMaterialInRfq =
                      cityIndex === lastCityIdx &&
                      siteIndex === lastSiteIdx &&
                      materialIndex === lastMaterialIdx;

                    const rfqCommercialTotals =
                      response1?.data?.data?.rfq_vendor_sub_total || [];

                    if (
                      isLastMaterialInRfq &&
                      rfqCommercialTotals.length !== 0
                    ) {
                      const vendorSubTotals = rfqCommercialTotals;

                      const materialTypes = vendorSubTotals[0]
                        ?.material_type_details
                        ? Object.keys(vendorSubTotals[0].material_type_details)
                        : [];

                      const entries = [
                        {
                          srNo: "A1",
                          keySuffix: "basic_price",
                          city: "Basic Price",
                        },
                        {
                          srNo: "B1",
                          keySuffix: "commission_charges",
                          city: "Commission Charges",
                        },
                        {
                          srNo: "C1",
                          keySuffix: "taxes",
                          city: "Taxes",
                        },
                        {
                          srNo: "D1",
                          keySuffix: "total_price",
                          city: "Total Price",
                        },
                        {
                          srNo: "E1",
                          keySuffix: "contingency",
                          city: "Contingency",
                        },
                        {
                          srNo: "F1",
                          keySuffix: "total_with_tax",
                          city: "Total With Tax",
                        },
                        {
                          srNo: "H1",
                          keySuffix: "payment_terms",
                          city: "Payment Terms",
                        },
                        {
                          srNo: "I1",
                          keySuffix: "payment_security_details",
                          city: "Payment Security Details",
                        },
                        {
                          srNo: "K1",
                          keySuffix: "commercial_ranking",
                          city: "Commercial Ranking on Total Price",
                        },
                      ];

                      if (vendorSubTotals.length > 0) {
                        entries.forEach((entry, entryIndex) => {
                          const rowData = {
                            key: `${entry.keySuffix}-${uniqueKeyCounter++}`,
                            city: entry.city,
                            srNo: entry.srNo,
                          };

                          let lowest = null;
                          let secondLowest = null;
                          let thirdLowest = null;

                          if (entry.keySuffix === "commercial_ranking") {
                            const roundDetails = vendorSubTotals
                              .map((vendorDetail) => {
                                const latestRound = findLatestAvailableRound(
                                  vendorDetail,
                                  vendorSubTotals
                                );

                                return vendorSubTotals.find(
                                  (v) =>
                                    v.vendor_name ===
                                      vendorDetail.vendor_name &&
                                    v.round === latestRound
                                );
                              })
                              .filter(Boolean);

                            const commercialRankingValues = roundDetails
                              .map((vendor) => vendor.commercial_ranking)
                              .filter((value) => typeof value === "number");

                            const sortedRankingValues = [
                              ...new Set(commercialRankingValues),
                            ].sort((a, b) => a - b);

                            lowest = sortedRankingValues[0] || null;
                            secondLowest = sortedRankingValues[1] || null;
                            thirdLowest = sortedRankingValues[2] || null;
                          }

                          const availableRounds = ["r", "r1", "r0"];

                          const validRounds = availableRounds.filter((round) =>
                            vendorSubTotals.some(
                              (v) => v.round === round && v.bid_date !== null
                            )
                          );

                          const latestRoundAvailable = availableRounds.find(
                            (round) =>
                              vendorSubTotals.some((v) => v.round === round)
                          );

                          const roundPriority = ["r1", "r", "r0"];

                          const latestRoundMap = {};

                          vendorSubTotals.forEach((detail) => {
                            const { vendor_id, round, basic_price } = detail;

                            if (basic_price === 0) return;

                            if (
                              !latestRoundMap[vendor_id] ||
                              roundPriority.indexOf(round) <
                                roundPriority.indexOf(
                                  latestRoundMap[vendor_id].round
                                )
                            ) {
                              latestRoundMap[vendor_id] = detail;
                            }
                          });

                          ["r0", "r1", "r"].forEach((round) => {
                            const roundDetails = vendorSubTotals.filter(
                              (vendorDetail) => vendorDetail.round === round
                            );

                            const filteredRoundDetails = roundDetails.filter(
                              (vendorDetail) => {
                                if (entry.keySuffix === "commercial_ranking") {
                                  const latest =
                                    latestRoundMap[vendorDetail.vendor_id];
                                  return latest && latest.round === round;
                                }
                                return true;
                              }
                            );

                            roundDetails.forEach((vendorDetail) => {
                              const vendorKey = vendorDetail.vendor_name
                                .toLowerCase()
                                .replace(/\s+/g, "");
                              if (
                                entry.keySuffix.startsWith(
                                  "material_type_details_mt_"
                                )
                              ) {
                                let mon = `${vendorKey}Amount${round.toUpperCase()}`;
                                const materialType = rowData.key
                                  .split("_mt_")[1]
                                  ?.split("-")[0];
                                let ju = `${vendorKey}Amount${round.toUpperCase()}`;
                              } else if (
                                entry.keySuffix.startsWith(
                                  "material_type_details_bag_"
                                )
                              ) {
                                const materialType = entry.materialType;
                                rowData[
                                  `${vendorKey}Amount${round.toUpperCase()}`
                                ] =
                                  vendorDetail.material_type_details?.[
                                    materialType
                                  ]?.average_unit_price_bag?.toLocaleString(
                                    "en-IN",
                                    {
                                      maximumFractionDigits: 2,
                                    }
                                  ) || "0";

                                rowData[`${vendorKey}Amount`] =
                                  vendorDetail.material_type_details?.[
                                    materialType
                                  ]?.average_unit_price_bag?.toLocaleString(
                                    "en-IN",
                                    {
                                      maximumFractionDigits: 2,
                                    }
                                  ) || "0";
                              }

                              rowData["uom"] =
                                typeof vendorDetail[entry.keySuffix] !==
                                  "string" &&
                                entry.keySuffix !== "contingency" &&
                                entry.keySuffix !== "commission_charges"
                                  ? ""
                                  : "";

                              rowData[
                                `${vendorKey}Amount${round.toUpperCase()}`
                              ] =
                                typeof vendorDetail[entry.keySuffix] !==
                                "string" ? (
                                  entry.keySuffix === "contingency" ||
                                  entry.keySuffix === "commission_charges" ? (
                                    `${vendorDetail[
                                      entry.keySuffix
                                    ]?.toLocaleString("en-IN", {
                                      maximumFractionDigits: 0,
                                    })}`
                                  ) : entry.keySuffix ===
                                      "commercial_ranking" &&
                                    vendorDetail[entry.keySuffix] ===
                                      secondLowest &&
                                    !vendorDetail.is_final_final_bid &&
                                    !vendorDetail.is_revised_final_bid &&
                                    !vendorDetail.is_initial_final_bid ? (
                                    <Tooltip
                                      color={"#094E8F"}
                                      placement="top"
                                      title={
                                        <span style={{ color: "#fff" }}>
                                          Second Lowest Price
                                        </span>
                                      }
                                    >
                                      <span className="secondLowest">L2</span>
                                    </Tooltip>
                                  ) : vendorDetail[entry.keySuffix] ===
                                      lowest &&
                                    !vendorDetail.is_final_final_bid &&
                                    !vendorDetail.is_revised_final_bid &&
                                    !vendorDetail.is_initial_final_bid ? (
                                    <Tooltip
                                      color={"#094E8F"}
                                      placement="top"
                                      title={
                                        <span style={{ color: "#fff" }}>
                                          Lowest Price
                                        </span>
                                      }
                                    >
                                      <span className="lowest">L1</span>
                                    </Tooltip>
                                  ) : entry.keySuffix ===
                                      "commercial_ranking" &&
                                    vendorDetail[entry.keySuffix] ===
                                      thirdLowest &&
                                    !vendorDetail.is_final_final_bid &&
                                    !vendorDetail.is_revised_final_bid &&
                                    !vendorDetail.is_initial_final_bid ? (
                                    <Tooltip
                                      color={"#094E8F"}
                                      placement="top"
                                      title={
                                        <span style={{ color: "#fff" }}>
                                          Third Lowest Price
                                        </span>
                                      }
                                    >
                                      <span className="thirdLowest">L3</span>
                                    </Tooltip>
                                  ) : (
                                    entry.keySuffix !== "commercial_ranking" &&
                                    `${vendorDetail[
                                      entry.keySuffix
                                    ]?.toLocaleString("en-IN", {
                                      maximumFractionDigits: 0,
                                    })}`
                                  )
                                ) : (
                                  entry.keySuffix !== "commercial_ranking" &&
                                  vendorDetail[entry.keySuffix]
                                );

                              rowData[`${vendorKey}Amount`] =
                                entry.keySuffix === "commercial_ranking" &&
                                vendorDetail[entry.keySuffix] === lowest &&
                                (!vendorDetail.is_final_final_bid ||
                                  !vendorDetail.is_revised_final_bid ||
                                  !vendorDetail.is_initial_final_bid) ? (
                                  <Tooltip
                                    color={"#094E8F"}
                                    placement="top"
                                    title={
                                      <span style={{ color: "#fff" }}>
                                        Lowest Price
                                      </span>
                                    }
                                  >
                                    <span className="lowest">L1</span>
                                  </Tooltip>
                                ) : vendorDetail[entry.keySuffix] ===
                                  secondLowest ? (
                                  <Tooltip
                                    color={"#094E8F"}
                                    placement="top"
                                    title={
                                      <span style={{ color: "#fff" }}>
                                        Second Lowest Price
                                      </span>
                                    }
                                  >
                                    <span className="secondLowest">L2</span>
                                  </Tooltip>
                                ) : vendorDetail[entry.keySuffix] ===
                                  thirdLowest ? (
                                  <Tooltip
                                    color={"#094E8F"}
                                    placement="top"
                                    title={
                                      <span style={{ color: "#fff" }}>
                                        Third Lowest Price
                                      </span>
                                    }
                                  >
                                    <span className="thirdLowest">L3</span>
                                  </Tooltip>
                                ) : (
                                  entry.keySuffix !== "commercial_ranking" &&
                                  (vendorDetail[
                                    entry.keySuffix
                                  ]?.toLocaleString("en-IN", {
                                    maximumFractionDigits: 0,
                                  }) ||
                                    0)
                                );
                            });
                          });

                          dataSource.push(rowData);
                        });
                      }
                    }

                    dataSource.forEach((item) => {
                      if (
                        item.srNo === "A1" ||
                        item.srNo === "L" ||
                        item.srNo === "L1"
                      )
                        return;

                      if (
                        item.items === materialDetail.rfq_material_type_name
                      ) {
                        let lowestPrice = Infinity;
                        let lowestVendorKey = null;
                        let lowestPriceKey = null;

                        const vendorKeys = Object.keys(item)
                          .filter(
                            (key) =>
                              key.endsWith("UnitPrice") && !key.includes("R")
                          )
                          .map((key) => key.replace("UnitPrice", ""));

                        vendorKeys.forEach((vendorKey) => {
                          const priceKeys = [
                            `${vendorKey}UnitPrice`,
                            `${vendorKey}UnitPriceR1`,
                            `${vendorKey}UnitPriceR0`,
                          ];

                          let latestKey = null;
                          let latestValue = Infinity;

                          for (const key of priceKeys) {
                            let val = item[key];

                            if (
                              typeof val === "object" &&
                              val?.props?.children
                            ) {
                              val = val.props.children;
                            }

                            const numericVal = parseFloat(
                              (val || "0").toString().replace(/,/g, "")
                            );
                            if (!isNaN(numericVal) && numericVal > 0) {
                              latestValue = numericVal;
                              latestKey = key;
                              break;
                            }
                          }

                          if (latestValue < lowestPrice) {
                            lowestPrice = latestValue;
                            lowestVendorKey = vendorKey;
                            lowestPriceKey = latestKey;
                          }
                        });

                        if (lowestPriceKey) {
                          item[lowestPriceKey] = (
                            <span
                              style={{
                                fontWeight: "bolder",
                                color: "green",
                              }}
                            >
                              {item[lowestPriceKey]?.props?.children ||
                                item[lowestPriceKey]}
                            </span>
                          );
                        }
                      }
                    });
                  }
                );

                return siteDetail.rfq_material_wise_details;
              }
            );
          }
        );

      setDataSource(dataSource);
      setData(dataSource);
      setSummaryDataSource(summaryDataSource);
      setVendors(vendors);

      if (state.material_type_names.length === 0) {
        const materialTypes =
          response1.data.data.comparative_details_by_rfq_site_wise.flatMap(
            (cityItem) =>
              cityItem.rfq_site_wise_details.flatMap((siteItem) =>
                siteItem.rfq_material_wise_details.map((materialItem) => ({
                  id: materialItem.rfq_material_type_id,
                  name: materialItem.rfq_material_type_name,
                }))
              )
          );

        const uniqueMaterialTypes = materialTypes.filter(
          (material, index, self) =>
            index ===
            self.findIndex(
              (m) => m.id === material.id && m.name === material.name
            )
        );
        setMaterialName(uniqueMaterialTypes);
      }
    } else {
      ErrorNotificationMsg(t("Error in Comparatives"));
    }

    setapiLoading(false);
  }, [response1, ohpPercent, estPrice]);

  const uniqueSummaryVendors = new Set();
  const summaryColumn = Array.isArray(vendors)
    ? vendors.flat(2).flatMap((material) => {
        const materialVendors = material.vendors || [];
        return materialVendors
          .filter((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            if (uniqueSummaryVendors.has(vendorKey)) {
              return false;
            } else {
              uniqueSummaryVendors.add(vendorKey);
              return true;
            }
          })
          .map((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            const bidDates = vendorBidDates[vendorKey] || {};

            const hasFinalBid =
              bidDates.finalBidDate && bidDates.finalBidDate !== "N/A";
            const hasRevisedBid =
              bidDates.revisedBidDate && bidDates.revisedBidDate !== "N/A";
            const hasInitialBid =
              bidDates.initialBidDate && bidDates.initialBidDate !== "N/A";

            let round = "";

            if (hasFinalBid) {
              round = "R - Final";
            } else if (!hasFinalBid && hasRevisedBid) {
              round = "R1 - Revised";
            } else {
              round = "R0 - Initial";
            }
            const color = " #094E8F";
            return {
              title: <span style={{ color }}>{vendor.vendor_name}</span>,
              children: [
                {
                  title: round,
                  align: "center",
                  children: [
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}Amount`,
                      key: `${vendorKey}Amount`,
                      align: "right",
                      render: (text, record, index) => {
                        return text;
                      },
                    },
                  ],
                },
              ],
            };
          });
      })
    : [];

  const uniqueVendors = new Set();
  const uniqueVendors2 = new Set();

  const venColumn = Array.isArray(vendors)
    ? vendors?.flat(2)?.flatMap((material) => {
        const materialVendors = material.vendors || [];

        return materialVendors
          .filter((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");
            if (uniqueVendors.has(vendorKey)) {
              return false;
            } else {
              uniqueVendors.add(vendorKey);
              return true;
            }
          })
          .map((vendor, venIndex) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            const bidDates = vendorBidDates[vendorKey] || {};
            const hasFinalBid =
              showR === false
                ? bidDates.finalBidDate && bidDates.finalBidDate !== "N/A"
                : // && (
                  //   vendor.is_initial_bid_final
                  // )
                  true;
            const hasRevisedBid =
              showR1 === false
                ? bidDates.finalBidDate === "N/A" &&
                  bidDates.revisedBidDate !== "N/A"
                : true;

            let hasInitialBid = true;

            if (!showR0) {
              if (vendor?.r0_price[0]?.is_initial_final_bid) {
                hasInitialBid = false;
              } else if (
                bidDates?.revisedBidDate === "N/A" &&
                bidDates?.initialBidDate !== "N/A"
              ) {
                hasInitialBid = true;
              } else {
                hasInitialBid = false;
              }
            }

            const shouldShowR1 = showR1 || hasRevisedBid;
            const shouldShowR0 = hasInitialBid || showR0;
            const shouldShowFinal = hasFinalBid || showR;

            const bidDetail = vendor.r_price[0] || {};
            const unitPrice = `₹ ${bidDetail.r_unit_price || 0}`;
            const amount = `₹ ${bidDetail.r_amount_with_gst || 0}`;

            const bidDetailR0 = vendor.r_price[0] || {};
            const unitPriceR0 = `₹ ${bidDetail.r_unit_price || 0}`;
            const amountR0 = `₹ ${bidDetail.r_amount_with_gst || 0}`;
            const color = " #094E8F";
            return {
              title: <span style={{ color }}>{vendor.vendor_name}</span>,
              className: "vencolumn",
              children: [
                shouldShowFinal && {
                  title: (
                    <>
                      <p>R - Final</p>
                      <p>{vendorBidDates[vendorKey]?.finalBidDate || "N/A"}</p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPrice`,
                      key: `${vendorKey}UnitPrice`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    categoryTree === "Earthing" && {
                      title: "Rs/Kg (Quoted)",
                      dataIndex: `${vendorKey}QuotedRsPerKg`,
                      key: `${vendorKey}QuotedRsPerKg`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}Amount`,
                      key: `${vendorKey}Amount`,
                      width: 80,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row, index) => {
                        const obj = {
                          children:
                            typeof value === "number"
                              ? value.toFixed(0)
                              : value,
                          props: {},
                        };
                        return obj;
                        // }
                      },
                    },
                  ].filter(Boolean),
                },
                // showR1 &&
                shouldShowR1 && {
                  title: (
                    <>
                      <p>R1 - Revised</p>
                      <p>
                        {vendorBidDates[vendorKey]?.revisedBidDate || "N/A"}
                      </p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR1`,
                      key: `${vendorKey}UnitPriceR1`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    categoryTree === "Earthing" && {
                      title: "Rs/Kg (Quoted)",
                      dataIndex: `${vendorKey}QuotedRsPerKgR1`,
                      key: `${vendorKey}QuotedRsPerKgR1`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR1`,
                      key: `${vendorKey}AmountR1`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row, index) => {
                        if (value === "L1") {
                          const obj = {
                            children: value,
                            props: {},
                          };
                          return obj;
                        } else {
                          const obj = {
                            children:
                              typeof value === "number"
                                ? value.toFixed(0)
                                : value,
                            props: {},
                          };
                          return obj;
                        }
                      },
                    },
                  ].filter(Boolean),
                },
                // showR0 &&
                shouldShowR0 && {
                  title: (
                    <>
                      <p>R0 - Initial</p>
                      <p>
                        {vendorBidDates[vendorKey]?.initialBidDate || "N/A"}
                      </p>
                    </>
                  ),

                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR0`,
                      key: `${vendorKey}UnitPriceR0`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    categoryTree === "Earthing" && {
                      title: "Rs/Kg (Quoted)",
                      dataIndex: `${vendorKey}QuotedRsPerKgR0`,
                      key: `${vendorKey}QuotedRsPerKgR0`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR0`,
                      key: `${vendorKey}AmountR0`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row, index) => {
                        const obj = {
                          children:
                            typeof value === "number"
                              ? value.toFixed(0)
                              : value,
                          props: {},
                        };

                        return obj;
                      },
                    },
                  ].filter(Boolean),
                },
              ].filter(Boolean),
            };
          });
      })
    : [];

  const hiddenvenColumn = Array.isArray(vendors)
    ? vendors?.flat(2)?.flatMap((material) => {
        const materialVendors = material.vendors || [];

        return materialVendors
          .filter((vendor) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");
            if (uniqueVendors2.has(vendorKey)) {
              return false;
            } else {
              uniqueVendors2.add(vendorKey);
              return true;
            }
          })
          .map((vendor, venIndex) => {
            const vendorKey = vendor.vendor_name
              .toLowerCase()
              .replace(/\s+/g, "");

            const bidDates = vendorBidDates[vendorKey] || {};

            const hasFinalBid =
              showR === false
                ? bidDates.finalBidDate && bidDates.finalBidDate !== "N/A"
                : true;
            const hasRevisedBid =
              showR1 === false
                ? bidDates.finalBidDate === "N/A" &&
                  bidDates.revisedBidDate !== "N/A"
                : true;
            const hasInitialBid =
              showR0 === false
                ? bidDates.revisedBidDate === "N/A" &&
                  bidDates.initialBidDate !== "N/A"
                : true;

            const shouldShowR1 = showR1 || hasRevisedBid;
            const shouldShowR0 = hasInitialBid || showR0;
            const shouldShowFinal = hasFinalBid || showR;

            const bidDetail = vendor.r_price[0] || {};
            const unitPrice = `₹ ${bidDetail.r_unit_price || 0}`;
            const amount = `₹ ${bidDetail.r_amount_with_gst || 0}`;

            const bidDetailR0 = vendor.r_price[0] || {};
            const unitPriceR0 = `₹ ${bidDetail.r_unit_price || 0}`;
            const amountR0 = `₹ ${bidDetail.r_amount_with_gst || 0}`;
            const color = " #094E8F";
            return {
              title: <span style={{ color }}>{vendor.vendor_name}</span>,
              className: "vencolumn",
              children: [
                {
                  title: (
                    <>
                      <p>R - Final</p>
                      <p>{vendorBidDates[vendorKey]?.finalBidDate || "N/A"}</p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPrice`,
                      key: `${vendorKey}UnitPrice`,
                      className: "vencolumn",
                      width: 80,
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}Amount`,
                      key: `${vendorKey}Amount`,
                      width: 80,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row, index) => {
                        const obj = {
                          children:
                            typeof value === "number"
                              ? value.toFixed(0)
                              : value,
                          props: {},
                        };
                        return obj;
                        // }
                      },
                    },
                  ],
                },
                {
                  title: (
                    <>
                      <p>R1 - Revised</p>
                      <p>
                        {vendorBidDates[vendorKey]?.revisedBidDate || "N/A"}
                      </p>
                    </>
                  ),
                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR1`,
                      key: `${vendorKey}UnitPriceR1`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };
                        return obj;
                      },
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR1`,
                      key: `${vendorKey}AmountR1`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row, index) => {
                        if (value === "L1") {
                          const obj = {
                            children: value,
                            props: {},
                          };
                          return obj;
                        } else {
                          const obj = {
                            children:
                              typeof value === "number"
                                ? value.toFixed(0)
                                : value,
                            props: {},
                          };
                          return obj;
                        }
                      },
                    },
                  ],
                },
                {
                  title: (
                    <>
                      <p>R0 - Initial</p>
                      <p>
                        {vendorBidDates[vendorKey]?.initialBidDate || "N/A"}
                      </p>
                    </>
                  ),

                  className: "vencolumn",
                  children: [
                    {
                      title: "Unit Price",
                      dataIndex: `${vendorKey}UnitPriceR0`,
                      key: `${vendorKey}UnitPriceR0`,
                      className: "vencolumn",
                      align: "right",
                      render: (value, row, index) => {
                        const obj = {
                          children: value,
                          props: {},
                        };

                        return obj;
                      },
                    },
                    {
                      title: "Basic Amount",
                      dataIndex: `${vendorKey}AmountR0`,
                      key: `${vendorKey}AmountR0`,
                      align: "right",
                      className: "vencolumn",
                      render: (value, row, index) => {
                        const obj = {
                          children:
                            typeof value === "number"
                              ? value.toFixed(0)
                              : value,
                          props: {},
                        };

                        return obj;
                      },
                    },
                  ],
                },
              ].filter(Boolean),
            };
          });
      })
    : [];

  const getCustomText = (text, record) => {
    if (record.city === "Basic Price") {
      return `${text}`;
    } else if (record.city === "Taxes") {
      return (
        <Tooltip
          color={"#094E8F"}
          placement="top"
          title={
            <span style={{ color: "#fff" }}>Sum of basic amount * GST</span>
          }
        >
          {`${text}`}
        </Tooltip>
      );
    } else if (record.city === "Commission Charges") {
      return (
        <Tooltip
          color={"#094E8F"}
          placement="top"
          title={
            <span style={{ color: "#fff" }}>
              Sum of basic amount * commission charge from business setting
            </span>
          }
        >
          {`${text}`}
        </Tooltip>
      );
    } else if (record.city === "Contingency") {
      return (
        <Tooltip
          color={"#094E8F"}
          placement="top"
          title={
            <span style={{ color: "#fff" }}>
              Sum of basic amount * Contingency charge from business setting
            </span>
          }
        >
          {text} @ {contengency}% on [
          <Tooltip
            color={"#094E8F"}
            placement="right"
            title={<span style={{ color: "#fff" }}>Total Evaluated Price</span>}
          >
            <span style={{ backgroundColor: "#FFD3B3", padding: "5px" }}>
              D1
            </span>
          </Tooltip>
          ]
        </Tooltip>
      );
    } else if (record.city === "Total Without Tax") {
      return (
        <>
          {text} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Basic Price</span>}
          >
            <span style={{ backgroundColor: "#A3F2EB", padding: "5px" }}>
              A1
            </span>
          </Tooltip>
          ]
        </>
      );
    } else if (record.city === "Commercial Ranking on Total Price") {
      return (
        <>
          {text} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Total Evaluated Price</span>}
          >
            <span style={{ backgroundColor: "#FFD3B3", padding: "5px" }}>
              D1
            </span>
          </Tooltip>
          ]
        </>
      );
    } else if (record.city === "Total Price") {
      return (
        <>
          {"Total Evaluated Price"} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Basic Price</span>}
          >
            <span style={{ backgroundColor: "#A3F2EB", padding: "5px" }}>
              A1
            </span>
          </Tooltip>{" "}
          +{" "}
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Commission Charges</span>}
          >
            <span style={{ backgroundColor: "#CCD5FF", padding: "5px" }}>
              B1
            </span>
          </Tooltip>{" "}
          +{" "}
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Taxes</span>}
          >
            <span style={{ backgroundColor: "#FFEFA8", padding: "5px" }}>
              C1
            </span>
          </Tooltip>
          ]
        </>
      );
    } else if (record.city === "Total With Tax") {
      return (
        <>
          {"Total Proposed Cost"} [
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Total Evaluated Price</span>}
          >
            <span style={{ backgroundColor: "#FFD3B3", padding: "5px" }}>
              D1
            </span>
          </Tooltip>{" "}
          +{" "}
          <Tooltip
            color={"#094E8F"}
            placement="top"
            title={<span style={{ color: "#fff" }}>Contingency</span>}
          >
            <span style={{ backgroundColor: "#FF9E96", padding: "5px" }}>
              E1
            </span>
          </Tooltip>
          ]
        </>
      );
    }
    return text;
  };

  const predefinedColors = {
    A1: "#A3F2EB",
    B1: "#CCD5FF",
    C1: "#FFEFA8",
    D1: "#FFD3B3",
    E1: "#FF9E96",
    F1: "#F4B9F4",
    G1: "#CCE5E5",
    H1: "#E1FF97",
    I1: "#ADFFC7",
    K1: "rgb(255 231 231)",
    L: "rgb(255 157 43)",
    L1: "rgb(225 127 43)",
    M: "rgb(155 157 3)",
    M1: "rgb(255 245 84)",
  };

  const getColor = (text) => {
    if (/^\d/.test(text)) {
      return null;
    }

    if (predefinedColors[text]) {
      return predefinedColors[text];
    }

    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 80%)`;
  };

  const parseEarthingStrip = (name) => {
    const match = name.match(/(\d+)\s*x\s*(\d+)\s*[-–]\s*(\w+)/i);
    if (!match) return null;
    return {
      width: parseFloat(match[1]),
      thickness: parseFloat(match[2]),
      material: match[3].toUpperCase(), // CU or GI
    };
  };

  const columns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      key: "srNo",
      width: 40,
      align: "center",
      render: (text) => {
        const bgColor = getColor(text);
        return (
          <span
            style={{
              backgroundColor: bgColor || "transparent",
              height: "25px",
              width: "25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Material",
      dataIndex: "items",
      key: "items",
      width: 160,
      render: (text, record, index) => {
        const a1Index = dataSource1.findIndex((row) => row.srNo === "A1");

        if (index >= a1Index && a1Index !== -1) {
          return {
            children: getCustomText(record.city || text, record),
            props: {
              colSpan: 1,
            },
          };
        }
        return text;
      },
    },
    showGST && {
      title: (
        <>
          <p>GST</p>
          <p>(%)</p>
        </>
      ),
      dataIndex: "gst_rate",
      key: "gst_rate",
      width: 40,
      align: "center",
    },
    {
      title: "Qty.",
      dataIndex: "qty",
      key: "qty",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record, index) => {
        return text?.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      },
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record) => {
        if (text === "Cubic Meter") {
          return "Cu. Mtr.";
        } else {
          return text;
        }
      },
    },
    categoryTree === "Earthing" && {
      title: "Weight Kg/meter",
      dataIndex: "wtKgPerMeter",
      key: "wtKgPerMeter",
      align: "center",
      // width: 40,
      width: 100,
      // width: "auto",
      render: (text, record) => {
        return text ? text.toFixed(3) : "";
      },
    },
    categoryTree === "Earthing" && {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title={
            <div style={{ fontSize: 12 }}>
              <div>CU : ₹ {estPrice.CU} / Kg</div>
              <div>GI : ₹ {estPrice.GI} / Kg</div>
            </div>
          }
        >
          <span>
            Est. Rate{" "}
            <InfoCircleOutlined
              style={{ color: "#094E8F", marginLeft: "3px", cursor:'pointer' }}
            />
          </span>
        </Tooltip>
      ),
      dataIndex: "estRate",
      key: "estRate",
      align: "center",
      width: 80,
      // width: "auto",
      render: (text, record) => {
        return text
          ? text.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            }) || 0
          : "";
      },
    },
    categoryTree === "Earthing" && {
      title: (
        <div style={{ textAlign: "center" }}>
          <div>Supplier Overhead & Profit</div>
          <Input
            type="number"
            min={0}
            max={100}
            value={ohpPercent}
            suffix="%"
            style={{ width: 80, padding: "1px 4px" }}
            onChange={(e) => {
              let value = e.target.value;
              if (value.length > 3) return;
              value = Number(value);
              if (value < 0 || value > 100) return;

              setOhpPercent(value);
            }}
          />
        </div>
      ),
      dataIndex: "ohp",
      key: "ohp",
      align: "center",
      width: 120,
      render: (text, record) => {
        return text
          ? text.toLocaleString("en-IN", {
              maximumFractionDigits: 0,
            }) || 0
          : "";
      },
    },
    showLPP && {
      title: "LPP (Last Purchase Price)",
      children: [
        {
          title: "Last order detail",
          children: [
            {
              title:
                // <>
                // <p>PO & </p>
                // <p>Date</p>

                // </>

                "PO & Date",
              dataIndex: "poDate",
              key: "poDate",
              width: 90,
              render: (text, record) => {
                if (!text) return "";
                const [poNumber, date] = text.split(" - ");

                return (
                  <>
                    <p style={{ fontSize: "12px" }}>{poNumber}</p>
                    <p style={{ fontSize: "12px" }}>{date}</p>
                  </>
                );
              },
            },
            {
              title: "Vendor Name",
              dataIndex: "vendorName",
              key: "vendorName",
              width: 90,
            },
            {
              title: "Unit Price",
              dataIndex: "unitPrice",
              key: "unitPrice",
              width: 80,
              align: "right",
            },
          ],
        },
        {
          title: (
            <Tooltip
              color="#094E8F"
              placement="top"
              title="Last 30 Days Avg. Price"
            >
              30DAP{" "}
              <InfoCircleOutlined
                style={{ color: "#094E8F", marginLeft: "3px" }}
              />
            </Tooltip>
          ),

          width: 100,
          align: "right",
          children: [
            {
              title: "",
              dataIndex: "avgPrice",
              key: "avgPrice",
              width: 100,
              align: "right",
            },
          ],
        },
      ],
    },
    {
      title: (
        <Tooltip color="#094E8F" placement="top" title="Item wise L1">
          IWL1{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      children: [
        {
          title: "",
          dataIndex: "itemWiseL1",
          key: "itemWiseL1",
          width: 100,
          align: "right",
          render: (text, record) => {
            return <strong>{text}</strong>;
          },
        },
      ],
    },
    categoryTree !== "Earthing" && {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title="Last 30 Days CityWise Avg. Price on Smart Buyer"
        >
          30DAP {""}{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      width: 80,
      children: [
        {
          title: "",
          dataIndex: "cityWise",
          width: 120,
          key: "cityWise",
          align: "right",
        },
      ],
    },
    ...venColumn,
  ].filter(Boolean);

  const hiddencolumns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      key: "srNo",
      width: 40,
      align: "center",
      render: (text) => {
        const bgColor = getColor(text);
        return (
          <span
            style={{
              backgroundColor: bgColor || "transparent",
              height: "25px",
              width: "25px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: "Material",
      dataIndex: "items",
      key: "items",
      width: 160,
      render: (text, record, index) => {
        const a1Index = dataSource1.findIndex((row) => row.srNo === "A1");

        if (index >= a1Index && a1Index !== -1) {
          return {
            children: getCustomText(record.city || text, record),
            props: { colSpan: 1 },
          };
        }
        return text;
      },
    },
    {
      title: "Qty.",
      dataIndex: "qty",
      key: "qty",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record, index) => {
        return text?.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      },
    },
    {
      title: "UoM.",
      dataIndex: "uom",
      key: "uom",
      align: "center",
      // width: 40,
      width: "auto",
      render: (text, record) => {
        if (text === "Cubic Meter") {
          return "Cu. Mtr.";
        } else {
          return text;
        }
      },
    },
    {
      title: (
        <Tooltip color="#094E8F" placement="top" title="Item wise L1">
          IWL1{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      children: [
        {
          title: "",
          dataIndex: "itemWiseL1",
          key: "itemWiseL1",
          width: 100,
          align: "right",
        },
      ],
    },
    {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title="Last 30 Days CityWise Avg. Price on Smart Buyer"
        >
          30DAP {""}{" "}
          <InfoCircleOutlined style={{ color: "#094E8F", marginLeft: "3px" }} />
        </Tooltip>
      ),
      width: 80,
      children: [
        {
          title: "",
          dataIndex: "cityWise",
          width: 120,
          key: "cityWise",
          align: "right",
        },
      ],
    },
    ...hiddenvenColumn,
  ].filter(Boolean);

  const handleCheckboxhideR1 = () => {
    setShowR0(!showR0);
  };

  const handleCheckboxhideR = () => {
    setShowR1(!showR1);
  };

  const handleCheckboxhideLPP = () => {
    setShowLPP(!showLPP);
  };

  const handleCheckboxhideGST = () => {
    setShowGST(!showGST);
  };

  const handleCheckboxhideRR = () => {
    setHideRBidPrice(!hideRBidPrice);
  };

  const updateState = async () => {
    try {
      setapiLoading(true);
      const filterPayload = buildGeneralComparativeFilterPayload(state);
      console.log("Comparative filter payload:", filterPayload);

      let response1 = STATIC_COMPARATIVE_RESPONSE;
      if (state.material_type_names?.length > 0) {
        const filtered = JSON.parse(JSON.stringify(STATIC_COMPARATIVE_RESPONSE));
        const siteBucket =
          filtered.data.data.comparative_details_by_rfq_site_wise[0]
            ?.rfq_site_wise_details?.[0];
        if (siteBucket?.rfq_material_wise_details) {
          siteBucket.rfq_material_wise_details =
            siteBucket.rfq_material_wise_details.filter((m) =>
              state.material_type_names.includes(m.rfq_material_type_name),
            );
        }
        response1 = filtered;
      }

      setResponse1(response1);
    } catch (error) {
      console.error("Failed to apply filters:", error);
      ErrorNotificationMsg(t("Error in Comparatives"));
    } finally {
      setapiLoading(false);
    }
  };

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
    } else {
      updateState();
    }
  }, [state]);

  const handleMaterialSelectChange = async (value) => {
    const updatedState2 = { ...state, material_type_names: value };
    setState(updatedState2);
  };

  const columns3 = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      key: "srNo",
    },
    {
      title: "",
      dataIndex: "title",
      key: "title",
      render: (text, record, index) => {
        return text?.toLocaleString("en-IN", { maximumFractionDigits: 0 });
      },
    },
    ...summaryColumn,
  ];

  const downloadExcel = async () => {
    const worksheet = XLSX.utils.json_to_sheet(dataSource1);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Table Data");
    XLSX.writeFile(workbook, `comparatives-rfq_city_wise${+new Date()}.xlsx`);
  };

  const exportToExcel = async () => {
    const canvas = await html2canvas(tableRef.current, { scale: 2 });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const headline = "Comparatives - City Wise";
    pdf.setFontSize(18);
    const textWidth = pdf.getTextWidth(headline);
    const centerX = (pageWidth - textWidth) / 2;
    const centerY = pageHeight / 2 - 20;

    pdf.text(headline, centerX, 20);
    const imgWidth = 190;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 10, 30, imgWidth, imgHeight);
    pdf.save(`Comparatives-${rfqNumber}`);
  };

  const exportToPdf = () => {
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });

    const pageWidth = pdf.internal.pageSize.getWidth();

    const headline = "Comparatives - City Wise";
    pdf.setFontSize(12);
    const textWidth = pdf.getTextWidth(headline);
    pdf.text(headline, (pageWidth - textWidth) / 2, 15);

    if (!tableRef.current) return;

    let headers = [];
    const headerCells = tableRef.current.querySelectorAll("thead tr th");

    let staticHeaders = ["Sr. No.", "City", "Site", "Item", "Quantity", "UoM"];
    headers.push(...staticHeaders);

    let vendorHeaders = [];
    headerCells.forEach((cell, index) => {
      let text = cell.innerText.trim();
      if (index >= staticHeaders.length) {
        text = text.replace(/\d{2}-\d{2}-\d{2}/g, "").trim(); // Remove date
        if (!text.includes("Unit Rate") && !text.includes("Basic Amount")) {
          vendorHeaders.push(`${text} - Unit Rate`);
          vendorHeaders.push(`${text} - Basic Amount`);
        }
      }
    });

    headers.push(...vendorHeaders);
    const data = [];
    const rows = tableRef.current.querySelectorAll("tbody tr");

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      const rowData = [];
      const cells = row.querySelectorAll("td");

      cells.forEach((cell, index) => {
        if (index < staticHeaders.length) {
          rowData.push(cell.innerText.trim());
        }
      });

      let vendorData = [];
      for (let i = staticHeaders.length; i < cells.length; i += 2) {
        const unitRate = cells[i]?.innerText.trim() || "-";
        const basicAmount = cells[i + 1]?.innerText.trim() || "-";
        vendorData.push(unitRate, basicAmount);
      }

      rowData.push(...vendorData);
      data.push(rowData);
    }

    pdf.autoTable({
      head: [headers],
      body: data,
      startY: 20,
      styles: { fontSize: 8, cellPadding: 3, overflow: "linebreak" },
      theme: "grid",
      precision: 100,
      headStyles: {
        fillColor: [0, 122, 255],
        textColor: 255,
        fontStyle: "bold",
      },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      margin: { top: 30, left: 10, right: 10 },
      columnStyles: {
        0: { cellWidth: 15 }, // Serial No.
        1: { cellWidth: 40 }, // City
        2: { cellWidth: 40 }, // Site
        3: { cellWidth: 30 }, // Item
        4: { cellWidth: 25 }, // Quantity
        5: { cellWidth: 25 }, // UoM
        ...vendorHeaders.reduce((cols, header, index) => {
          cols[staticHeaders.length + index] = { cellWidth: 25 };
          return cols;
        }, {}),
      },
      didDrawPage: (data) => {
        pdf.setFontSize(10);
        pdf.setTextColor(0);
      },
    });

    pdf.save(`comparatives_city_wise_${Date.now()}.pdf`);
  };

  const menu = (
    <Menu className="downloadmenu">
      <Menu.ItemGroup
        key="pdf"
        title={
          <span style={{ fontWeight: "bold" }}>
            <img src={PDF} alt="PDF Icon" /> Download PDF
          </span>
        }
      >
        <Menu.Item key="pdf1" onClick={exportToExcel}>
          1.1 Download as per current view
        </Menu.Item>
      </Menu.ItemGroup>

      <Menu.ItemGroup
        key="excel"
        title={
          <span style={{ fontWeight: "bold" }}>
            <img src={Excel} alt="PDF Icon" />
            Download Excel
          </span>
        }
      >
        <DownloadTableExcel
          filename={`Comparative City Wise Statement-${rfqNumber}`}
          sheet="users"
          currentTableRef={tableRef.current}
        >
          <Menu.Item key="excel2">2.1. Download as per current view</Menu.Item>
        </DownloadTableExcel>

        <Menu.Item key="excel2">
          <DownloadTableExcel
            filename={`Comparative City Wise Statement-${rfqNumber}`}
            sheet="users"
            currentTableRef={hiddenTableRef.current}
          >
            2.2. Download all offers (BIDs)
          </DownloadTableExcel>
        </Menu.Item>
      </Menu.ItemGroup>
    </Menu>
  );

  const handleExcelDownload = async () => {
    const endpoint = `comparative-rfq/${id}/comparative-excel-material-download/`;
    const fileType = "csv";
    try {
      await getRequest(endpoint)
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "text/csv",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = `comparatives-rfq_${+new Date()}.${fileType}`;
          link.click();
        })
        .catch((error) => {
          //ErrorNotificationMsg(t(`err_${fileType}_download`));
        });
    } catch (error) {
      //setBtnloading(false);
      //ErrorNotificationMsg(error);
    }
  };

  function parseEarthingMaterial(name = "") {
    const sizeMatch = name.match(/(\d+)\s*x\s*(\d+)/i);
    const material = name.toUpperCase().includes("CU")
      ? "CU"
      : name.toUpperCase().includes("GI")
      ? "GI"
      : "";

    return {
      width: sizeMatch ? Number(sizeMatch[1]) : 0,
      thickness: sizeMatch ? Number(sizeMatch[2]) : 0,
      material,
    };
  }

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>Comparatives - Material Wise</h3>

            <Breadcrumb className="Breadcrumb-Sec">
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">Home</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/general-procurement-list">General Procurement</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>Comparatives - Material Wise</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <h5 className="rfq-number new">
            RFQ Number :{" "}
            <span style={{ marginRight: "20px" }}>{rfqNumber}</span>
            Buyer : <span>{buyerName}</span>
          </h5>
          
          <div className="btnwrp2" style={{ alignItems: "center" }}>
            <h5 className="rfq-number new">Amount in INR</h5>
            <Dropdown overlay={menu} trigger={["hover"]} placement="bottom">
              <Button
                className="SaveGradiantBtn"
                type="primary"
                htmlType="PDF"
                label="PDF"
                icon={<img src={Downloadpd} alt="PDF Icon" />}
              >
                Download
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="stepscontentwrap">
        <div className="stepsContent">
          <section className="grid-sec mt-5 ">
            <div className="PR-body" style={{ padding: "0" }}>
              <div
                className="rgq-num-wrap"
                style={{ flexWrap: "wrap", alignItems: "center" }}
              >
                <div className="bidleftfilters">
                  <Checkbox checked={showR0} onChange={handleCheckboxhideR1}>
                    Show Initial BID Price - R0
                  </Checkbox>
                  <Checkbox checked={showR1} onChange={handleCheckboxhideR}>
                    Show Revised BID Price - R1
                  </Checkbox>
                  <Checkbox checked={showLPP} onChange={handleCheckboxhideLPP}>
                    Show Last Purchase Price
                  </Checkbox>
                  <Checkbox checked={showGST} onChange={handleCheckboxhideGST}>
                    Show GST %
                  </Checkbox>
                </div>
                {
                  categoryTree === "Earthing" && (
                    <div
                className="bidlRightfilters"
                  style={{ flexWrap: "nowrap" }}
              >
                  <label>Cu (Price)</label>
                  <Input
                    type="number"
                    min={0}
                    style={{ width: "80px", margin: "0 10px" }}
                    value={estPrice.CU}
                    onChange={(e) => {
                      if (e.target.value.length > 5 || e.target.value < 0)
                        return;
                      const value =
                        e.target.value === "" ? 0 : Number(e.target.value);
                      setEstPrice({ ...estPrice, CU: value });
                    }}
                  />
                  <label>GI (Price)</label>
                  <Input
                    type="number"
                    min={0}
                    style={{ width: "80px", margin: "0 10px" }}
                    value={estPrice.GI}
                    onChange={(e) => {
                      if (e.target.value.length > 5 || e.target.value < 0)
                        return;
                      const value =
                        e.target.value === "" ? 0 : Number(e.target.value);
                      setEstPrice({ ...estPrice, GI: value });
                    }}
                  />
                </div>
                  )
                }
                
                
                <div
                  className="bidlRightfilters"
                  style={{ flexWrap: "nowrap" }}
                >
                  <label>Material Filter</label>
                  <Form.Item name="material_type" label="">
                    <Select
                      showArrow
                      mode="multiple"
                      showSearch
                      allowClear
                      placeholder="Select Material"
                      optionFilterProp="children"
                      onChange={handleMaterialSelectChange}
                      value={state.material_type_names}
                      style={{ width: "200px" }}
                    >
                      {materialName.map((material) => (
                        <Option key={material.id} value={material.name}>
                          {material.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                  
                </div>
              </div>

              <Spin spinning={apiLoading}>
                <div ref={tableRef}>
                  <Table
                    ref={tableRef}
                    dataSource={dataSource1}
                    bordered
                    scroll={{ x: "auto" }}
                    columns={columns}
                    className="comparativecustomtbl"
                    pagination={false}
                  />
                </div>
              </Spin>
              <div className="footerTable">
                <Table
                  columns={columns3}
                  dataSource={summaryDataSource}
                  pagination={false}
                  bordered
                />
              </div>
            </div>
            <div ref={hiddenTableRef} style={{ display: "none" }}>
              <Table
                ref={hiddenTableRef}
                dataSource={dataSource1}
                bordered
                scroll={{ x: "auto" }}
                columns={hiddencolumns}
                className="comparativecustomtbl"
                pagination={false}
                // sticky
              />
            </div>
          </section>
        </div>
        <div
          className="FormFooter bidftr"
          style={{ padding: "16px  ", margin: "0" }}
        >
          <ButtonField
            className="SaveGradiantBtn"
            type="primary"
            label="Price Comparative Review"
            onClick={() => {
              const payload = buildGeneralComparativeSubmitPayload({
                rfqId: id,
                state,
              });
              console.log("Comparative submit payload:", payload);
              // await patchRequest(`general-procurement-comparative/${id}/`, payload);
            }}
          />
        </div>
      </div>
    </>
  );
};

export default GeneralProcurementComparative;
